import { ButtonGroup, IconButton, Tooltip } from "@mui/material";
import { useEffect, useRef, useState } from "react";

interface FormGroupPageScrollItem {
  id: string;
  title?: string;
}

interface FormGroupPageScrollProps {
  groups: FormGroupPageScrollItem[];
  activeId: string;
}

const ITEM_HEIGHT = 36;
const MAX_VISIBLE_ITEMS = 15;

// Jump-to-section nav rail for a schema-based content type's form, one stop per
// form_group — the dynamic-schema counterpart to CreateQuiz's ContentPageScroll.
// CreateQuiz's rail maps a fixed SVG to each of its hardcoded sections; a schema's
// form_groups are arbitrary per content type and carry no icon of their own, so
// each stop is just its 1-based position instead of an icon.
const FormGroupPageScroll = ({ groups, activeId }: FormGroupPageScrollProps) => {
  const [activeScroll, setActiveScroll] = useState(activeId);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (activeId !== activeScroll) {
      setActiveScroll(activeId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  // Keep the active stop visible inside the rail's own scroll area as the page
  // scrolls (scroll-spy) or a stop is clicked — `block: "nearest"` only moves
  // this rail's own scrollbar, not the page's.
  useEffect(() => {
    if (!activeScroll) return;
    const activeButton = containerRef.current?.querySelector<HTMLElement>(
      `[data-nav-id="${activeScroll}"]`,
    );
    if (typeof activeButton?.scrollIntoView === "function") {
      activeButton.scrollIntoView({ block: "nearest" });
    }
  }, [activeScroll]);

  if (!groups?.length) {
    return null;
  }

  const handleScrollToView = (id: string) => {
    setActiveScroll(id);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const formatPosition = (position: number) => String(position + 1).padStart(2, "0");

  return (
    <ButtonGroup
      ref={containerRef}
      orientation='vertical'
      sx={{
        alignItems: "center",
        maxHeight: ITEM_HEIGHT * MAX_VISIBLE_ITEMS,
        overflowY: "auto",
        backgroundColor: "#ffffff",
        borderRadius: 0,
        boxShadow: "0 10px 25px 0 rgba(0, 0, 0, 0.12)",
      }}>
      {groups.map((group, position) => (
        <Tooltip title={group?.title || `Section ${position + 1}`} key={group.id} placement='left'>
          <IconButton
            onClick={() => handleScrollToView(group.id)}
            data-testid='form-group-page-scroll-item'
            data-nav-id={group.id}
            aria-label={group?.title || `Go to section ${position + 1}`}
            sx={{
              width: "100%",
              height: ITEM_HEIGHT,
              justifyContent: "center",
              fontSize: "16px",
              borderRadius: 0,
              fontWeight: activeScroll === group.id ? 700 : 400,
              color: activeScroll === group.id ? "#1976d2" : "inherit",
              backgroundColor: activeScroll === group.id ? "#D7ECFD" : "transparent",
              ":hover": {
                backgroundColor: activeScroll === group.id ? "#D7ECFD" : "#f5f6f8",
              },
            }}>
            {formatPosition(position)}
          </IconButton>
        </Tooltip>
      ))}
    </ButtonGroup>
  );
};

export default FormGroupPageScroll;
